import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MainCard from 'components/MainCard';
import useGet from 'hooks/useGet';
import apiClient from 'utils/apiClient';

const getLocalDateTimeInput = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const tzOffset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - tzOffset).toISOString().slice(0, 16);
};

const splitToArray = (value, transform = (item) => item) =>
  String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
    .map(transform);

const normalizeCollection = (data, keys = []) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  for (const key of keys) {
    if (Array.isArray(data[key])) return data[key];
  }
  return [];
};

const toIdString = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return String(value?._id || value?.id || value);
};

const getSubcategoryCollection = (category) => {
  if (!category || typeof category !== 'object') return [];
  if (Array.isArray(category.subCategories)) return category.subCategories;
  if (Array.isArray(category.subcategories)) return category.subcategories;
  if (Array.isArray(category.children)) return category.children;
  return [];
};

const getParentIdFromSubcategoryValue = (value) => {
  if (!value || typeof value !== 'object') return '';
  return toIdString(
    value.parentCategoryId ||
      value.parentCategory ||
      value.categoryId ||
      value.category ||
      value.parentId
  );
};

const emptyForm = {
  code: '',
  name: '',
  description: '',
  discountType: 'percentage',
  discountValue: '',
  maxDiscountAmount: '',
  minOrderAmount: '0',
  validFrom: getLocalDateTimeInput(new Date()),
  validUntil: '',
  isActive: true,
  firstOrderOnly: false,
  currencies: '',
  applyOn: 'all',
  categoryIds: [],
  subCategoryIds: [],
  totalUsageLimit: '',
  perUserUsageLimit: ''
};

const applyOnOptions = [
  { value: 'all', label: 'All products' },
  { value: 'products', label: 'Specific products' },
  { value: 'categories', label: 'Specific categories' },
  { value: 'subcategories', label: 'Specific subcategories' }
];

function SectionBlock({ title, subtitle, rightSlot, children, sx = {} }) {
  return (
    <Box
      sx={{
        p: { xs: 2.5, md: 3 },
        borderRadius: 2.5,
        border: '1px solid',
        borderColor: 'grey.200',
        bgcolor: '#fff',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.02), 0 4px 12px rgba(0, 0, 0, 0.03)',
        ...sx
      }}
    >
      <Stack spacing={2.5}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, pb: 1.5, borderBottom: '1px solid', borderColor: 'grey.100' }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.975rem' }}>
              {title}
            </Typography>
            {subtitle ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, fontSize: '0.8rem' }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
          {rightSlot ? <Box>{rightSlot}</Box> : null}
        </Box>
        {children}
      </Stack>
    </Box>
  );
}

function VoucherPreview({ formData, categoryNameById, subcategoryNameById }) {
  const isPercentage = formData.discountType === 'percentage';
  const discountFormatted = isPercentage
    ? `${formData.discountValue || '0'}% OFF`
    : `₹${Number(formData.discountValue || 0).toLocaleString()} OFF`;

  let targetDisplay = 'All products';
  if (formData.applyOn === 'categories' && formData.categoryIds?.length > 0) {
    const firstCat = categoryNameById.get(formData.categoryIds[0]) || 'Category';
    targetDisplay = formData.categoryIds.length > 1
      ? `${firstCat} +${formData.categoryIds.length - 1} more`
      : firstCat;
  } else if (formData.applyOn === 'subcategories' && formData.subCategoryIds?.length > 0) {
    const firstSub = subcategoryNameById.get(formData.subCategoryIds[0]) || 'Subcategory';
    targetDisplay = formData.subCategoryIds.length > 1
      ? `${firstSub} +${formData.subCategoryIds.length - 1} more`
      : firstSub;
  } else if (formData.applyOn === 'products') {
    targetDisplay = 'Specific Products';
  }

  return (
    <Box
      sx={{
        borderRadius: 2.5,
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 60%, #334155 100%)',
        color: '#fff',
        p: 2.5,
        position: 'relative',
        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.25)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: -20,
          right: -20,
          width: 80,
          height: 80,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255, 215, 0, 0.25) 0%, rgba(255, 215, 0, 0) 70%)',
          pointerEvents: 'none'
        }}
      />

      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="overline" sx={{ letterSpacing: 1.5, color: '#94a3b8', fontWeight: 700, fontSize: 10 }}>
            SHIRISH SPARKLE GEMS
          </Typography>
          <Chip
            size="small"
            label={formData.isActive ? 'Active' : 'Inactive'}
            color={formData.isActive ? 'success' : 'default'}
            sx={{ height: 20, fontSize: 10, fontWeight: 700 }}
          />
        </Stack>

        <Box sx={{ py: 0.5 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              letterSpacing: -0.5,
              background: 'linear-gradient(90deg, #ffffff 0%, #cbd5e1 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {discountFormatted}
          </Typography>
          <Typography variant="body2" sx={{ color: '#cbd5e1', fontWeight: 500, mt: 0.5 }}>
            {formData.name || 'Promotional Discount'}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: 'rgba(255, 255, 255, 0.08)',
            border: '1px dashed rgba(255, 255, 255, 0.25)',
            borderRadius: 1.5,
            px: 2,
            py: 1,
          }}
        >
          <Box>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 9, textTransform: 'uppercase', letterSpacing: 1 }}>
              Coupon Code
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, letterSpacing: 1.5, color: '#facc15' }}>
              {formData.code ? formData.code.toUpperCase() : 'COUPONCODE'}
            </Typography>
          </Box>
          <ConfirmationNumberOutlinedIcon sx={{ color: 'rgba(255, 255, 255, 0.6)' }} />
        </Box>

        <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.12)' }} />
        <Stack spacing={0.5}>
          <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: 11 }}>
            • Min order: <b style={{ color: '#e2e8f0' }}>₹{formData.minOrderAmount || '0'}</b>
            {isPercentage && formData.maxDiscountAmount ? ` • Max cap: ₹${formData.maxDiscountAmount}` : ''}
          </Typography>
          <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: 11 }}>
            • Applies to: <b style={{ color: '#e2e8f0' }}>{targetDisplay}</b>
          </Typography>
          {formData.validUntil && (
            <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: 11, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AccessTimeIcon sx={{ fontSize: 12 }} />
              Expires {new Date(formData.validUntil).toLocaleDateString()}
            </Typography>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}

export default function CouponForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState(emptyForm);
  const [productIdsText, setProductIdsText] = useState('');
  const [productFieldText, setProductFieldText] = useState('');
  const [productFieldTouched, setProductFieldTouched] = useState(false);
  const [subCategoryParentId, setSubCategoryParentId] = useState('');
  const [subCategoryFallbackNames, setSubCategoryFallbackNames] = useState({});
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const pageTopRef = useRef(null);

  const scrollToTop = () => {
    // Scroll element into view and smooth scroll window & root containers
    if (pageTopRef.current) {
      pageTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    if (typeof document !== 'undefined') {
      document.documentElement?.scrollTo({ top: 0, behavior: 'smooth' });
      document.body?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const detailEndpoint = useMemo(() => (isEdit ? `/api/coupons/admin/${id}` : null), [id, isEdit]);
  const { data: couponData, loading: loadingCoupon, error: loadError } = useGet(detailEndpoint);
  const { data: categoriesData } = useGet('/api/categories');
  const { data: productsData } = useGet('/api/products/');

  const categories = useMemo(
    () => normalizeCollection(categoriesData, ['categories', 'data', 'items']),
    [categoriesData]
  );
  const products = useMemo(
    () => normalizeCollection(productsData, ['products', 'data', 'items']),
    [productsData]
  );

  const subcategoryEndpoint = useMemo(
    () => (subCategoryParentId ? `/api/categories/subcategories/${subCategoryParentId}?all=true` : null),
    [subCategoryParentId]
  );
  const { data: subcategoriesData } = useGet(subcategoryEndpoint);
  const subcategories = useMemo(
    () => normalizeCollection(subcategoriesData, ['subCategories', 'subcategories', 'data', 'items']),
    [subcategoriesData]
  );

  const categoryNameById = useMemo(() => {
    const map = new Map();
    categories.forEach((category) => {
      const idValue = toIdString(category);
      if (!idValue) return;
      map.set(idValue, category?.name || category?.categoryName || category?.title || idValue);
    });
    return map;
  }, [categories]);

  const subcategoryNameById = useMemo(() => {
    const map = new Map();
    Object.entries(subCategoryFallbackNames).forEach(([idValue, nameValue]) => {
      if (!idValue) return;
      map.set(idValue, nameValue || idValue);
    });
    categories.forEach((category) => {
      getSubcategoryCollection(category).forEach((subcategory) => {
        const idValue = toIdString(subcategory);
        if (!idValue) return;
        map.set(idValue, subcategory?.name || subcategory?.title || idValue);
      });
    });
    subcategories.forEach((subcategory) => {
      const idValue = toIdString(subcategory);
      if (!idValue) return;
      map.set(idValue, subcategory?.name || subcategory?.title || idValue);
    });
    return map;
  }, [categories, subcategories, subCategoryFallbackNames]);

  const productLabelById = useMemo(() => {
    const map = new Map();
    products.forEach((product) => {
      const idValue = toIdString(product);
      if (!idValue) return;
      const sku = product?.sku || product?.SKU || '';
      if (sku) map.set(idValue, sku);
    });
    return map;
  }, [products]);

  const productIdBySku = useMemo(() => {
    const map = new Map();
    products.forEach((product) => {
      const idValue = toIdString(product);
      const sku = String(product?.sku || product?.SKU || '').trim();
      if (!idValue || !sku) return;
      map.set(sku.toLowerCase(), idValue);
    });
    return map;
  }, [products]);

  const handleProductInputChange = (event) => {
    const inputValue = event.target.value;
    setProductFieldText(inputValue);
    setProductFieldTouched(true);

    if (!isEdit) {
      setProductIdsText(inputValue);
      return;
    }

    const resolvedIds = splitToArray(inputValue, (item) => {
      const token = String(item || '').trim();
      if (!token) return '';
      return productIdBySku.get(token.toLowerCase()) || token;
    });
    setProductIdsText(resolvedIds.join(', '));
  };

  useEffect(() => {
    if (!isEdit || !couponData?.coupon) return;
    const coupon = couponData.coupon;
    setFormData({
      code: coupon.code || '',
      name: coupon.name || '',
      description: coupon.description || '',
      discountType: coupon.discountType || 'percentage',
      discountValue: String(coupon.discountValue ?? ''),
      maxDiscountAmount: coupon.maxDiscountAmount === null || coupon.maxDiscountAmount === undefined ? '' : String(coupon.maxDiscountAmount),
      minOrderAmount: String(coupon.minOrderAmount ?? 0),
      validFrom: getLocalDateTimeInput(coupon.validFrom),
      validUntil: getLocalDateTimeInput(coupon.validUntil),
      isActive: Boolean(coupon.isActive),
      firstOrderOnly: Boolean(coupon.firstOrderOnly),
      currencies: Array.isArray(coupon.currencies) ? coupon.currencies.join(', ') : '',
      applyOn: coupon.applyOn || 'all',
      categoryIds: Array.isArray(coupon.categoryIds) ? coupon.categoryIds.map((item) => toIdString(item)).filter(Boolean) : [],
      subCategoryIds: Array.isArray(coupon.subCategoryIds)
        ? coupon.subCategoryIds.map((item) => toIdString(item)).filter(Boolean)
        : [],
      totalUsageLimit: coupon.usageLimit?.total ? String(coupon.usageLimit.total) : '',
      perUserUsageLimit: coupon.usageLimit?.perUser ? String(coupon.usageLimit.perUser) : ''
    });

    const productIds = Array.isArray(coupon.productIds) ? coupon.productIds.map((item) => toIdString(item)).filter(Boolean) : [];
    setProductIdsText(productIds.join(', '));
    setProductFieldText(productIds.join(', '));
    setProductFieldTouched(false);

    let inferredParentId = '';
    if (Array.isArray(coupon.categoryIds) && coupon.categoryIds.length > 0) {
      inferredParentId = toIdString(coupon.categoryIds[0]);
    }
    if (!inferredParentId && Array.isArray(coupon.subCategoryIds) && coupon.subCategoryIds.length > 0) {
      inferredParentId = getParentIdFromSubcategoryValue(coupon.subCategoryIds[0]);
    }
    if (inferredParentId) {
      setSubCategoryParentId(inferredParentId);
    }
  }, [couponData, isEdit]);

  useEffect(() => {
    if (!isEdit || productFieldTouched) return;
    if (formData.applyOn !== 'products') return;
    if (!productFieldText) return;
    const withSku = splitToArray(productFieldText, (token) => productLabelById.get(token) || token).join(', ');
    if (withSku !== productFieldText) setProductFieldText(withSku);
  }, [formData.applyOn, isEdit, productFieldText, productFieldTouched, productLabelById]);

  useEffect(() => {
    if (formData.applyOn !== 'subcategories') return;
    if (subCategoryParentId) return;
    if (!formData.subCategoryIds.length || !categories.length) return;

    const selectedSet = new Set(formData.subCategoryIds);
    const inferredCategory = categories.find((category) =>
      getSubcategoryCollection(category).some((subcategory) => selectedSet.has(toIdString(subcategory)))
    );

    if (inferredCategory) {
      const inferredId = toIdString(inferredCategory);
      if (inferredId) setSubCategoryParentId(inferredId);
    }
  }, [categories, formData.applyOn, formData.subCategoryIds, subCategoryParentId]);

  useEffect(() => {
    if (formData.applyOn !== 'subcategories') return;
    if (subCategoryParentId) return;
    if (!formData.subCategoryIds.length || !categories.length) return;

    let isCancelled = false;
    const selectedIds = new Set(formData.subCategoryIds);

    const resolveParentAndNames = async () => {
      const fallbackNames = {};
      for (const category of categories) {
        const categoryId = toIdString(category);
        if (!categoryId) continue;

        try {
          const response = await apiClient.get(`/api/categories/subcategories/${categoryId}?all=true`);
          const list = normalizeCollection(response?.data, ['subCategories', 'subcategories', 'data', 'items']);

          list.forEach((subcategory) => {
            const idValue = toIdString(subcategory);
            if (!idValue || !selectedIds.has(idValue)) return;
            fallbackNames[idValue] = subcategory?.name || subcategory?.title || idValue;
          });

          const hasSelected = list.some((subcategory) => selectedIds.has(toIdString(subcategory)));
          if (hasSelected) {
            if (!isCancelled) {
              setSubCategoryParentId(categoryId);
              setSubCategoryFallbackNames((prev) => ({ ...prev, ...fallbackNames }));
            }
            return;
          }
        } catch (error) {
          // Ignore per-category fetch errors; continue with remaining categories.
        }
      }

      if (!isCancelled && Object.keys(fallbackNames).length) {
        setSubCategoryFallbackNames((prev) => ({ ...prev, ...fallbackNames }));
      }
    };

    resolveParentAndNames();
    return () => {
      isCancelled = true;
    };
  }, [categories, formData.applyOn, formData.subCategoryIds, subCategoryParentId]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setErrorMessage('');
    setSuccessMessage('');
    setFormData((prev) => {
      const next = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };

      if (name === 'applyOn') {
        if (value !== 'categories') next.categoryIds = [];
        if (value !== 'subcategories') next.subCategoryIds = [];
      }

      return next;
    });
  };

  const handleMultiSelectChange = (name) => (event) => {
    const nextValue = event.target.value;
    setErrorMessage('');
    setSuccessMessage('');
    setFormData((prev) => ({
      ...prev,
      [name]: Array.isArray(nextValue) ? nextValue : []
    }));
  };

  const buildPayload = () => {
    const payload = {
      code: formData.code,
      name: formData.name,
      description: formData.description,
      discountType: formData.discountType,
      discountValue: Number(formData.discountValue),
      minOrderAmount: formData.minOrderAmount,
      validFrom: formData.validFrom,
      validUntil: formData.validUntil,
      isActive: formData.isActive,
      firstOrderOnly: formData.firstOrderOnly,
      applyOn: formData.applyOn,
      currencies: splitToArray(formData.currencies, (item) => item.toUpperCase()),
      totalUsageLimit: formData.totalUsageLimit,
      perUserUsageLimit: formData.perUserUsageLimit
    };

    payload.maxDiscountAmount = formData.maxDiscountAmount === '' ? null : Number(formData.maxDiscountAmount);

    if (formData.applyOn === 'products') payload.productIds = splitToArray(productIdsText);
    if (formData.applyOn === 'categories') payload.categoryIds = formData.categoryIds;
    if (formData.applyOn === 'subcategories') payload.subCategoryIds = formData.subCategoryIds;

    return payload;
  };

  const validate = () => {
    if (!formData.code.trim() || !formData.name.trim()) return 'Code and name are required.';
    if (!formData.validUntil) return 'Valid until date is required.';

    const discount = Number(formData.discountValue);
    if (!Number.isFinite(discount) || discount <= 0) return 'Discount value must be a positive number.';

    if (formData.discountType === 'percentage' && discount > 100) {
      return 'Percentage discount cannot exceed 100.';
    }

    if (formData.applyOn === 'products' && splitToArray(productIdsText).length === 0) {
      return 'At least one product id is required for product-specific coupon.';
    }

    if (formData.applyOn === 'categories' && formData.categoryIds.length === 0) {
      return 'Select at least one category.';
    }

    if (formData.applyOn === 'subcategories' && formData.subCategoryIds.length === 0) {
      return 'Select at least one subcategory.';
    }

    return '';
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationError = validate();
    if (validationError) {
      setErrorMessage(validationError);
      scrollToTop();
      return;
    }

    setSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');
    try {
      const payload = buildPayload();
      if (isEdit) {
        await apiClient.patch(`/api/coupons/admin/${id}`, payload);
        setSuccessMessage('Coupon updated successfully.');
        scrollToTop();
      } else {
        await apiClient.post('/api/coupons/admin', payload);
        navigate('/coupons');
        return;
      }
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || 'Failed to save coupon.');
      scrollToTop();
    } finally {
      setSubmitting(false);
    }
  };

  const fieldProps = { fullWidth: true, size: 'small' };

  return (
    <Stack spacing={3} sx={{ pb: 6 }}>
      {/* 📍 Top scroll target */}
      <div ref={pageTopRef} style={{ position: 'relative', top: -20 }} />

      {/* 🌟 PAGE HEADER */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2,
          bgcolor: '#fff',
          p: { xs: 2, sm: 2.5 },
          borderRadius: 2.5,
          border: '1px solid',
          borderColor: 'grey.200',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
        }}
      >
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <Button
              variant="text"
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/coupons')}
              sx={{ color: 'text.secondary', fontWeight: 600, p: 0.5, minWidth: 'auto' }}
            >
              Coupons
            </Button>
            <Typography variant="body2" color="text.secondary">/</Typography>
            <Typography variant="body2" color="text.primary" fontWeight={600}>
              {isEdit ? (formData.code || 'Edit') : 'Create'}
            </Typography>
            {isEdit && (
              <Chip
                size="small"
                color={formData.isActive ? 'success' : 'default'}
                label={formData.isActive ? 'Active' : 'Inactive'}
                sx={{ height: 22, fontWeight: 700, fontSize: 11, ml: 1 }}
              />
            )}
          </Stack>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: -0.5 }}>
            {isEdit ? `Edit Coupon: ${formData.name || formData.code || ''}` : 'Create New Coupon'}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.5} alignItems="center">
          <Button
            variant="outlined"
            onClick={() => navigate('/coupons')}
            disabled={submitting}
            sx={{ borderRadius: 2, textTransform: 'none', px: 2.5, borderColor: 'grey.300', color: 'text.primary' }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="coupon-form"
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={submitting}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 3,
              fontWeight: 700,
              bgcolor: '#1e293b',
              '&:hover': { bgcolor: '#0f172a' },
              boxShadow: '0 4px 12px rgba(15, 23, 42, 0.25)'
            }}
          >
            {submitting ? 'Saving...' : isEdit ? 'Update Coupon' : 'Create Coupon'}
          </Button>
        </Stack>
      </Box>

      {loadError ? <Alert severity="error">Failed to load coupon details.</Alert> : null}
      {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
      {successMessage ? <Alert severity="success">{successMessage}</Alert> : null}

      {isEdit && loadingCoupon ? (
        <Box p={4} textAlign="center" bgcolor="#fff" borderRadius={2.5}>
          <Typography>Loading coupon details...</Typography>
        </Box>
      ) : (
        <Box component="form" id="coupon-form" onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* ⬅️ LEFT COLUMN: Main Configurations */}
            <Grid size={{ xs: 12, md: 7, lg: 8 }}>
              <Stack spacing={3}>
                {/* 1. Identity */}
                <SectionBlock title="Identity" subtitle="Primary coupon code, public name, and messaging.">
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        {...fieldProps}
                        label="Coupon Code"
                        name="code"
                        value={formData.code}
                        onChange={(e) => {
                          handleChange({
                            target: {
                              name: 'code',
                              value: e.target.value.toUpperCase().replace(/\s+/g, '')
                            }
                          });
                        }}
                        placeholder="e.g. SPARKLE20"
                        required
                        helperText="Uppercase alphanumeric characters, no spaces"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        {...fieldProps}
                        label="Coupon Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="e.g. Festival Sparkle Special"
                        required
                      />
                    </Grid>
                    <Grid size={12}>
                      <TextField
                        {...fieldProps}
                        multiline
                        minRows={2}
                        label="Description (Optional)"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        placeholder="Provide details about terms or internal notes..."
                      />
                    </Grid>
                  </Grid>
                </SectionBlock>

                {/* 2. Discount Setup */}
                <SectionBlock
                  title="Discount Setup"
                  subtitle="Configure discount rate, caps, and the validity window."
                  rightSlot={
                    <Chip
                      size="small"
                      color={formData.discountType === 'percentage' ? 'primary' : 'secondary'}
                      label={formData.discountType === 'percentage' ? 'Percentage' : 'Fixed Amount'}
                      sx={{ fontWeight: 600 }}
                    />
                  }
                >
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField {...fieldProps} select label="Discount Type" name="discountType" value={formData.discountType} onChange={handleChange}>
                        <MenuItem value="percentage">Percentage (%)</MenuItem>
                        <MenuItem value="fixed">Fixed amount (Amt)</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        {...fieldProps}
                        type="number"
                        label="Discount Value"
                        name="discountValue"
                        value={formData.discountValue}
                        onChange={handleChange}
                        required
                        InputProps={{
                          endAdornment: <InputAdornment position="end">{formData.discountType === 'percentage' ? '%' : 'Amt'}</InputAdornment>
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        {...fieldProps}
                        type="number"
                        label="Max Discount Cap"
                        name="maxDiscountAmount"
                        value={formData.maxDiscountAmount}
                        onChange={handleChange}
                        disabled={formData.discountType !== 'percentage'}
                        helperText={formData.discountType === 'percentage' ? 'Maximum discount cap (optional)' : 'Applies to percentage discount only'}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        {...fieldProps}
                        type="number"
                        label="Minimum Eligible Order Amount"
                        name="minOrderAmount"
                        value={formData.minOrderAmount}
                        onChange={handleChange}
                        helperText="Minimum cart total needed to redeem"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        {...fieldProps}
                        type="datetime-local"
                        label="Valid From"
                        name="validFrom"
                        InputLabelProps={{ shrink: true }}
                        value={formData.validFrom}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        {...fieldProps}
                        type="datetime-local"
                        label="Valid Until"
                        name="validUntil"
                        InputLabelProps={{ shrink: true }}
                        value={formData.validUntil}
                        onChange={handleChange}
                        required
                      />
                    </Grid>
                  </Grid>
                </SectionBlock>

                {/* 3. Targeting */}
                <SectionBlock
                  title="Targeting & Scope"
                  subtitle="Choose what products or categories this coupon applies to."
                  rightSlot={
                    <Chip
                      size="small"
                      variant="outlined"
                      label={applyOnOptions.find((item) => item.value === formData.applyOn)?.label || 'All products'}
                      sx={{ fontWeight: 600 }}
                    />
                  }
                >
                  <Grid container spacing={2}>
                    <Grid size={12}>
                      <TextField {...fieldProps} select label="Apply On" name="applyOn" value={formData.applyOn} onChange={handleChange}>
                        {applyOnOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    {formData.applyOn === 'products' ? (
                      <Grid size={12}>
                        <TextField
                          {...fieldProps}
                          label={isEdit ? 'Product SKUs (comma separated)' : 'Product IDs (comma separated)'}
                          name="productIds"
                          value={productFieldText}
                          onChange={handleProductInputChange}
                          helperText={isEdit ? 'Editable as SKU. Payload still sends product IDs.' : 'Enter product IDs separated by commas'}
                        />
                      </Grid>
                    ) : null}

                    {formData.applyOn === 'categories' ? (
                      <Grid size={12}>
                        <FormControl fullWidth size="small">
                          <InputLabel id="coupon-categories-label">Categories</InputLabel>
                          <Select
                            labelId="coupon-categories-label"
                            multiple
                            value={formData.categoryIds}
                            label="Categories"
                            onChange={handleMultiSelectChange('categoryIds')}
                            renderValue={(selected) =>
                              selected.map((idValue) => categoryNameById.get(idValue) || idValue).join(', ')
                            }
                          >
                            {categories.map((category) => {
                              const idValue = toIdString(category);
                              return (
                                <MenuItem key={idValue} value={idValue}>
                                  {category?.name || category?.categoryName || category?.title || idValue}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                      </Grid>
                    ) : null}

                    {formData.applyOn === 'subcategories' ? (
                      <>
                        <Grid size={{ xs: 12, sm: 5 }}>
                          <TextField
                            select
                            {...fieldProps}
                            label="Parent Category"
                            value={subCategoryParentId}
                            onChange={(event) => {
                              setSubCategoryParentId(event.target.value);
                              setFormData((prev) => ({ ...prev, subCategoryIds: [] }));
                            }}
                            helperText="Select parent category first"
                          >
                            {categories.map((category) => {
                              const idValue = toIdString(category);
                              return (
                                <MenuItem key={idValue} value={idValue}>
                                  {category?.name || category?.categoryName || category?.title || idValue}
                                </MenuItem>
                              );
                            })}
                          </TextField>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 7 }}>
                          <FormControl fullWidth size="small">
                            <InputLabel id="coupon-subcategories-label">Subcategories</InputLabel>
                            <Select
                              labelId="coupon-subcategories-label"
                              multiple
                              value={formData.subCategoryIds}
                              label="Subcategories"
                              onChange={handleMultiSelectChange('subCategoryIds')}
                              renderValue={(selected) =>
                                selected.map((idValue) => subcategoryNameById.get(idValue) || idValue).join(', ')
                              }
                            >
                              {subcategories.map((subcategory) => {
                                const idValue = toIdString(subcategory);
                                return (
                                  <MenuItem key={idValue} value={idValue}>
                                    {subcategory?.name || subcategory?.title || idValue}
                                  </MenuItem>
                                );
                              })}
                            </Select>
                          </FormControl>
                        </Grid>
                      </>
                    ) : null}
                  </Grid>
                </SectionBlock>
              </Stack>
            </Grid>

            {/* ➡️ RIGHT COLUMN: Live Voucher Preview & Rules */}
            <Grid size={{ xs: 12, md: 5, lg: 4 }}>
              <Stack spacing={3}>
                {/* 1. Live Customer Preview */}
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary', mb: 1, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 11 }}>
                    Customer View Preview
                  </Typography>
                  <VoucherPreview
                    formData={formData}
                    categoryNameById={categoryNameById}
                    subcategoryNameById={subcategoryNameById}
                  />
                </Box>

                {/* 2. Status & Access */}
                <SectionBlock title="Status & Eligibility" subtitle="Control coupon availability and limits.">
                  <Stack spacing={2}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 1.5,
                        bgcolor: '#f8fafc',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'grey.200'
                      }}
                    >
                      <Box>
                        <Typography variant="body2" fontWeight={700}>
                          Active Status
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Allow customers to redeem
                        </Typography>
                      </Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.isActive}
                            name="isActive"
                            onChange={handleChange}
                            color="success"
                          />
                        }
                        label=""
                        sx={{ m: 0 }}
                      />
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 1.5,
                        bgcolor: '#f8fafc',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'grey.200'
                      }}
                    >
                      <Box>
                        <Typography variant="body2" fontWeight={700}>
                          First Order Only
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Restrict to new shoppers
                        </Typography>
                      </Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.firstOrderOnly}
                            name="firstOrderOnly"
                            onChange={handleChange}
                            color="primary"
                          />
                        }
                        label=""
                        sx={{ m: 0 }}
                      />
                    </Box>
                  </Stack>
                </SectionBlock>

                {/* 3. Limits & Currency */}
                <SectionBlock title="Usage Limits & Currency" subtitle="Set usage thresholds and currency filters.">
                  <Stack spacing={2}>
                    <TextField
                      {...fieldProps}
                      type="number"
                      label="Total Usage Limit"
                      name="totalUsageLimit"
                      value={formData.totalUsageLimit}
                      onChange={handleChange}
                      placeholder="Leave blank for unlimited"
                      helperText="Total redemptions allowed in store"
                    />
                    <TextField
                      {...fieldProps}
                      type="number"
                      label="Per User Usage Limit"
                      name="perUserUsageLimit"
                      value={formData.perUserUsageLimit}
                      onChange={handleChange}
                      placeholder="e.g. 1"
                      helperText="Redemptions allowed per customer"
                    />
                    <TextField
                      {...fieldProps}
                      label="Currencies (comma separated)"
                      name="currencies"
                      value={formData.currencies}
                      onChange={handleChange}
                      placeholder="INR, USD"
                      helperText="Leave blank to allow all currencies"
                    />
                  </Stack>
                </SectionBlock>
              </Stack>
            </Grid>
          </Grid>

          {/* 📌 STICKY FOOTER ACTION BAR */}
          <Box
            sx={{
              position: 'sticky',
              bottom: 16,
              zIndex: 10,
              borderRadius: 2.5,
              border: '1px solid',
              borderColor: 'grey.300',
              bgcolor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
              px: { xs: 2, md: 3 },
              py: 1.75,
              mt: 3
            }}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2} alignItems={{ sm: 'center' }}>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                <Chip size="small" color={formData.isActive ? 'success' : 'default'} label={formData.isActive ? 'Active' : 'Inactive'} sx={{ fontWeight: 600 }} />
                <Chip size="small" variant="outlined" label={formData.discountType === 'percentage' ? `${formData.discountValue || 0}% OFF` : `₹${formData.discountValue || 0} OFF`} />
                <Chip size="small" variant="outlined" label={applyOnOptions.find((item) => item.value === formData.applyOn)?.label || 'All products'} />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 1, fontWeight: 500 }}>
                  {formData.code || 'COUPON'}
                </Typography>
              </Stack>

              <Stack direction="row" spacing={1.5}>
                <Button
                  variant="outlined"
                  size="medium"
                  onClick={() => navigate('/coupons')}
                  disabled={submitting}
                  sx={{ borderRadius: 2, textTransform: 'none', px: 2.5, borderColor: 'grey.300', color: 'text.primary' }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  size="medium"
                  startIcon={<SaveIcon />}
                  disabled={submitting}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    px: 3,
                    fontWeight: 700,
                    bgcolor: '#1e293b',
                    '&:hover': { bgcolor: '#0f172a' },
                    boxShadow: '0 4px 12px rgba(15, 23, 42, 0.25)'
                  }}
                >
                  {submitting ? 'Saving...' : isEdit ? 'Update Coupon' : 'Create Coupon'}
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Box>
      )}
    </Stack>
  );
}
